import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Globe, Lock, Plus, RefreshCw, Shield } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  canConnectFundGeniusDomain,
  FUNDGENIUS_DOMAINS,
  getFundGeniusDomainEntitlement,
} from '../../../config/fundgeniusDomains';
import { isFundGeniusAgency } from '../../../utils/agencyType';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const DOMAIN_TYPE_OPTIONS = [
  { value: 'intake', label: 'Intake Portal', cname: FUNDGENIUS_DOMAINS.INTAKE_DOMAIN },
  { value: 'app', label: 'App/Login Portal', cname: FUNDGENIUS_DOMAINS.APP_DOMAIN },
  { value: 'redirect', label: 'Redirect Domain', cname: FUNDGENIUS_DOMAINS.PUBLIC_DOMAIN },
];

/**
 * FundGenius Domains & Redirects panel.
 * Shown for fundgenius agencies instead of the credit-repair ConnectDomainModal flow.
 */
const FundGeniusDomainsSection = ({
  agency,
  agencyId,
  isReadOnly,
  onUpdate,
}) => {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [verifyingId, setVerifyingId] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [domainType, setDomainType] = useState('intake');
  const [domain, setDomain] = useState('');

  const planKey = agency?.pricing_plan || agency?.plan || 'starter';
  const entitlement = useMemo(() => getFundGeniusDomainEntitlement(planKey), [planKey]);
  const domains = agency?.settings?.fundgenius_domains || [];
  const canConnect = canConnectFundGeniusDomain(planKey, domainType);
  const selectedType = DOMAIN_TYPE_OPTIONS.find((item) => item.value === domainType) || DOMAIN_TYPE_OPTIONS[0];

  const refreshLocalDomains = (nextDomains) => {
    onUpdate?.({
      settings: {
        ...(agency?.settings || {}),
        fundgenius_domains: nextDomains,
      },
    });
  };

  const handleSaveDomain = async () => {
    if (isReadOnly) return;
    if (!canConnect) {
      toast.error('Upgrade your plan to connect this domain type.');
      return;
    }
    if (!domain.trim()) {
      toast.error('Enter a domain hostname');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/fundgenius/domains`,
        {
          agency_id: agencyId,
          domain: domain.trim().toLowerCase(),
          domain_type: domainType,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const created = response.data?.domain;
      const next = [...domains.filter((d) => d.id !== created?.id), created].filter(Boolean);
      refreshLocalDomains(next);
      toast.success('Domain saved. Add the CNAME record, then verify DNS.');
      setShowConnect(false);
      setDomain('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (domainId) => {
    setVerifyingId(domainId);
    try {
      const response = await axios.post(
        `${API}/fundgenius/domains/${domainId}/verify?agency_id=${encodeURIComponent(agencyId)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const verified = response.data?.domain;
      const next = domains.map((item) => (item.id === domainId ? verified : item));
      refreshLocalDomains(next);
      toast.success('Domain verified');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyingId(null);
    }
  };

  if (!isFundGeniusAgency(agency)) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="fundgenius-domains-section">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
            <Globe className="text-cyan-700" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Domains & Redirects</h2>
            <p className="text-sm text-slate-500">
              Manage custom domains for your FundGenius app and intake portal
            </p>
          </div>
          <Badge className="ml-auto bg-cyan-100 text-cyan-800 border-0">
            <Crown size={12} className="mr-1" />
            {entitlement.label}
          </Badge>
        </div>
      </div>

      <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
        <h3 className="font-semibold text-cyan-950">FundGenius Domain Setup</h3>
        <p className="mt-1 text-sm text-cyan-800">
          Connect a custom domain for your funding intake or app portal.
          Official hosts remain {FUNDGENIUS_DOMAINS.PUBLIC_DOMAIN}, {FUNDGENIUS_DOMAINS.APP_DOMAIN}, and {FUNDGENIUS_DOMAINS.INTAKE_DOMAIN}.
        </p>
      </div>

      {!canConnectFundGeniusDomain(planKey, 'intake') && !canConnectFundGeniusDomain(planKey, 'app') ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center" data-testid="fundgenius-domains-upgrade">
          <Lock className="mx-auto mb-3 text-slate-400" size={28} />
          <h3 className="text-lg font-semibold text-slate-900">No Domains Connected</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
            Starter plans use {FUNDGENIUS_DOMAINS.INTAKE_DOMAIN}/{'{agencySlug}'}. Upgrade to Pro or White Label to connect a custom domain.
          </p>
          <Badge className="mt-4 bg-amber-100 text-amber-700 border-0">
            <Crown size={12} className="mr-1" />
            Upgrade to unlock custom domains
          </Badge>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-slate-900">
              {domains.length ? 'Connected Domains' : 'No Domains Connected'}
            </h3>
            {!isReadOnly ? (
              <Button onClick={() => setShowConnect(true)} data-testid="fundgenius-connect-domain">
                <Plus size={16} className="mr-2" />
                Connect a Domain
              </Button>
            ) : null}
          </div>

          {domains.length ? (
            <div className="space-y-3">
              {domains.map((item) => (
                <div key={item.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
                  <div>
                    <div className="font-medium text-slate-900">{item.domain}</div>
                    <div className="text-xs text-slate-500">
                      {item.domain_type} · CNAME → {item.cname_target || selectedType.cname}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="border-0 bg-slate-100 text-slate-700">{item.status || 'pending_dns'}</Badge>
                    {!isReadOnly ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={verifyingId === item.id}
                        onClick={() => handleVerify(item.id)}
                      >
                        {verifyingId === item.id ? <RefreshCw size={14} className="animate-spin" /> : <Shield size={14} />}
                        <span className="ml-2">Verify DNS</span>
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {showConnect ? (
            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="fundgenius-connect-domain-modal">
              <h4 className="text-lg font-semibold text-slate-900">Connect FundGenius Domain</h4>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Domain type</label>
                <Select value={domainType} onValueChange={setDomainType}>
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {DOMAIN_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Domain</label>
                <Input
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="funding.myagency.com"
                  data-testid="fundgenius-domain-input"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Agency</label>
                <Input value={agency?.name || agencyId} disabled />
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                <p className="font-medium text-slate-800">DNS instructions</p>
                <p className="mt-1">CNAME host: subdomain of your domain</p>
                <p>CNAME target: {selectedType.cname}</p>
                {!canConnect ? (
                  <p className="mt-2 text-amber-700">Your current plan cannot connect this domain type.</p>
                ) : null}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowConnect(false)}>Cancel</Button>
                <Button onClick={handleSaveDomain} disabled={saving || !canConnect}>
                  {saving ? 'Saving…' : 'Save Domain'}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default FundGeniusDomainsSection;
